import { useForwardedRef } from "@hooks/use-forwarded-ref"
import clsx from "clsx"
import type { ClipboardEventHandler, ForwardedRef, InputHTMLAttributes } from "react"
import { useLayoutEffect } from "react"

import type { FocusableProps } from "../types/events"
import type { InputBase, TextInputBase, ValueBase } from "../types/inputs"
import { inputStyle } from "./input"

export type TextareaProps<T extends string | null | undefined = string> = Omit<
	InputHTMLAttributes<HTMLTextAreaElement>,
	"onChange" | "onPaste" | "value"
> &
	InputBase &
	ValueBase<T | null | undefined> &
	TextInputBase &
	FocusableProps & {
		ref?: ForwardedRef<HTMLTextAreaElement>
		isDynamic?: boolean
		isMonospaced?: boolean
		rows?: number
		onPaste?: ClipboardEventHandler<HTMLTextAreaElement>
		className?: string
		preventLineBreaks?: boolean
		disabled?: boolean
		required?: boolean
	}

export function Textarea<T extends string | null | undefined = string>(props: TextareaProps<T>) {
	const {
		disabled,
		isDisabled: pIsDisabled = false,
		required = false,
		isInvalid = false,
		isReadOnly = false,
		isDynamic = false,
		isMonospaced = false,
		preventLineBreaks = false,
		rows,
		onChange,
		onPaste,
		className,
		ref,
		...otherProps
	} = props
	const inputRef = useForwardedRef(ref ?? null)
	const isDisabled = disabled || pIsDisabled

	// biome-ignore lint/correctness/useExhaustiveDependencies: update height on value change
	useLayoutEffect(() => {
		const input = inputRef.current
		if (input && isDynamic) {
			input.style.height = "auto"
			input.style.height = `${input.scrollHeight}px`
		}
	}, [inputRef, isDynamic, props.value])

	return (
		<textarea
			required={required}
			ref={inputRef}
			rows={isDynamic ? 1 : rows}
			className={clsx(
				inputStyle({
					readOnly: isReadOnly,
					disabled: isDisabled,
					invalid: isInvalid,
				}),
				"min-h-[28px] w-full",
				{
					"overflow-hidden resize-none": isDynamic,
					"font-mono": isMonospaced,
				},
				className,
			)}
			{...otherProps}
			value={props.value ?? undefined}
			defaultValue={props.defaultValue ?? undefined}
			disabled={isDisabled}
			readOnly={isReadOnly}
			onPaste={onPaste}
			onChange={({ target: { value } }) => {
				onChange?.((preventLineBreaks ? (value ?? "").replace(/\r?\n|\r/g, "") : value) as T)
			}}
		/>
	)
}
